import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiRefreshCw, FiMonitor, FiFileText, FiChevronLeft, FiChevronRight, FiCamera, FiGrid, FiClock } from 'react-icons/fi';
import { useNdTheme } from '../utils/ndTheme';
import '../styles/newDesign.css';
import { toCyrillicFio, matchesLocalReportSearch, localReportSearchLabels } from '../utils/latinToCyrillicFio';
import { hideDuplicateFioRows, countHiddenFioDuplicates } from '../utils/hideDuplicateFioRows';
import UserWorkTimeDetailsModal from './Modals/UserWorkTimeDetailsModal';
import UserWorkTimeDetailsMobile from './UserWorkTimeDetailsMobile';
import AppUsageModal from './Modals/AppUsageModal';
import AppUsageMobile from './AppUsageMobile';
import RemoteWorktimeReportModal from './Modals/RemoteWorktimeReportModal';
import RemoteWorktimeReportMobile from './RemoteWorktimeReportMobile';
import OneCDocumentHistoryMobile from './OneCDocumentHistoryMobile';
import { ENABLE_MODAL_SWIPE_BACK } from '../utils/mobileModalSwipe';
import WorkTimeHub from './WorkTimeHub';

// Вынесем парсер вне функции
function parseDate(str) {
  if (!str) return null;
  // Если ISO-формат
  if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) return new Date(str);
  // Если DD.MM.YYYY HH:mm:ss
  const match = str.match(/(\d{2})\.(\d{2})\.(\d{4})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    const [_, dd, mm, yyyy, hh, min, ss] = match;
    // В JS месяцы с 0, поэтому -1
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(ss));
  }
  // Если только дата без времени
  const matchDate = str.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (matchDate) {
    const [_, dd, mm, yyyy] = matchDate;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  }
  // Падение в стандартный парсер
  return new Date(str);
}

function formatTime(dtStr) {
  if (!dtStr) return '';

  // Как в WorkTimeReportModal: сначала ISO с Z — это момент в UTC, показываем в локали браузера.
  // Иначе regex для «голого» YYYY-MM-DD HH:mm:ss ошибочно взял бы часы из UTC как локальные (+сдвиг).
  const isUTC = /Z$/.test(dtStr) || (dtStr.includes('T') && dtStr.includes('Z'));

  if (isUTC) {
    const d = new Date(dtStr);
    if (isNaN(d.getTime())) return dtStr;
    return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU')}`;
  }

  const match = dtStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const d = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    if (!isNaN(d.getTime())) {
      return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU')}`;
    }
  }

  const d = new Date(dtStr);
  if (isNaN(d.getTime())) return dtStr;
  return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU')}`;
}

function formatWorkTime(mins) {
  if (mins === 0) return '0м';
  if (!mins || mins < 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h > 0 ? h + 'ч ' : ''}${m}м`;
}

// Функция для получения сегодняшней даты в локальном времени (киевское)
function getTodayLocalDate() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function WorkTimeMobile({ open, onClose, onOpenMobileSidebar }) {
  // ВЕРСИЯ 5.0 - ДОБАВЛЕН realUsername, УЛУЧШЕНО ЛОГИРОВАНИЕ API
  console.log('%c🚨🚨🚨 КОМПОНЕНТ WorkTimeMobile V5.0 - BUILD 2025-01-20 🚨🚨🚨', 'background: #ff0000; color: #ffffff; font-size: 18px; font-weight: bold; padding: 8px;');
  console.log('🚨🚨🚨 [WorkTimeMobile] ====== КОМПОНЕНТ V5.0 - BUILD 2025-01-20 ======');
  console.log('🚨🚨🚨 [WorkTimeMobile] ЕСЛИ ВЫ ВИДИТЕ ЭТОТ ЛОГ - НОВЫЙ КОД ЗАГРУЖЕН! 🚨🚨🚨');
  
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const modalRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => {
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  });
  
  const [selectedUser, setSelectedUser] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  // Используем локальное время (киевское), а не UTC
  const [startDate, setStartDate] = useState(() => getTodayLocalDate());
  const [endDate, setEndDate] = useState(() => getTodayLocalDate());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsModal, setDetailsModal] = useState({ 
    open: false, 
    logs: [], 
    username: '',
    realUsername: '', // Правильный username для API запросов
    activityStats: null,
    urls: [],
    applications: [],
    screenshots: [],
    startDate: '',
    endDate: '',
  });
  const [importing, setImporting] = useState(false);
  const [importOk, setImportOk] = useState(null);
  const [showAppUsage, setShowAppUsage] = useState(false);
  const [showRemoteWorktime, setShowRemoteWorktime] = useState(false);
  const [showLocalReport, setShowLocalReport] = useState(false);
  const [showOneCHistoryModal, setShowOneCHistoryModal] = useState(false);
  
  // Состояния для модалки локального отчета
  // По умолчанию устанавливаем сегодняшнюю дату (используем локальное время, а не UTC)
  const getTodayDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [localReportCurrentDate, setLocalReportCurrentDate] = useState(getTodayDate());
  const [localReportStartDate, setLocalReportStartDate] = useState(getTodayDate());
  const [localReportEndDate, setLocalReportEndDate] = useState(getTodayDate());
  const [localReportSearchTerm, setLocalReportSearchTerm] = useState('');
  const [localReportUsers, setLocalReportUsers] = useState([]);
  const [localReportUsersList, setLocalReportUsersList] = useState([]);
  const [localReportUserOptions, setLocalReportUserOptions] = useState([]);
  const prefetchedLocalReport = useRef({ date: null, report: null });
  const [localReportShowAutocomplete, setLocalReportShowAutocomplete] = useState(false);
  const [loadingLocalReport, setLoadingLocalReport] = useState(false);
  const [localReportDetailsModal, setLocalReportDetailsModal] = useState({
    open: false,
    logs: [],
    username: '',
    realUsername: '',
    activityStats: null,
    urls: [],
    applications: [],
    screenshots: [],
    startDate: '',
    endDate: ''
  });
  const [firstLoginFromApplications, setFirstLoginFromApplications] = useState({});
  const [lastLogoutFromApplications, setLastLogoutFromApplications] = useState({});
  const [detailsAvailability, setDetailsAvailability] = useState({});

  useEffect(() => {
    if (open) {
      fetch('/api/worktime-users')
        .then(res => res.json())
        .then(data => {
          setUsersList(data.users || []);
          setUserOptions(data.users || []);
        })
        .catch(() => {
          setUsersList([]);
          setUserOptions([]);
        });
    }
  }, [open]);

  // Загрузка списка пользователей для локального отчета
  useEffect(() => {
    if (showLocalReport) {
      fetch('/api/worktime-users')
        .then(res => res.json())
        .then(data => {
          setLocalReportUsersList(data.users || []);
          setLocalReportUserOptions(data.users || []);
        })
        .catch(() => {
          setLocalReportUsersList([]);
          setLocalReportUserOptions([]);
        });
    }
  }, [showLocalReport]);

  // Префетч отчёта за сегодня при открытии модалки (чтобы при клике «Отчет активности локальных ПК» данные уже были)
  useEffect(() => {
    if (!open) return;
    const today = getTodayDate();
    fetch(`/api/local-worktime-report?start=${today}&end=${today}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.report)) {
          prefetchedLocalReport.current = { date: today, report: data.report };
        }
      })
      .catch(() => {});
  }, [open]);

  // Навигация по дням стрелками для локального отчета
  const handleLocalReportPreviousDay = () => {
    const date = new Date(localReportCurrentDate);
    date.setDate(date.getDate() - 1);
    const newDate = date.toISOString().split('T')[0];
    setLocalReportCurrentDate(newDate);
    setLocalReportStartDate(newDate);
    setLocalReportEndDate(newDate);
    // Автоматически загружаем отчет при изменении даты стрелками
    setLoadingLocalReport(true);
    fetch(`/api/local-worktime-report?start=${newDate}&end=${newDate}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.report)) {
          setLocalReportUsers(data.report);
        } else {
          setLocalReportUsers([]);
        }
      })
      .catch(error => {
        console.error('Ошибка загрузки локального отчета:', error);
        setLocalReportUsers([]);
      })
      .finally(() => {
        setLoadingLocalReport(false);
      });
  };

  const handleLocalReportNextDay = () => {
    const date = new Date(localReportCurrentDate);
    date.setDate(date.getDate() + 1);
    const newDate = date.toISOString().split('T')[0];
    setLocalReportCurrentDate(newDate);
    setLocalReportStartDate(newDate);
    setLocalReportEndDate(newDate);
    // Автоматически загружаем отчет при изменении даты стрелками
    setLoadingLocalReport(true);
    fetch(`/api/local-worktime-report?start=${newDate}&end=${newDate}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.report)) {
          setLocalReportUsers(data.report);
        } else {
          setLocalReportUsers([]);
        }
      })
      .catch(error => {
        console.error('Ошибка загрузки локального отчета:', error);
        setLocalReportUsers([]);
      })
      .finally(() => {
        setLoadingLocalReport(false);
      });
  };

  // Загрузка отчета при изменении дат (префетч за сегодня, если есть). forceRefresh = true — всегда с сервера (кнопка «Показать отчет», автообновление).
  const fetchLocalReport = React.useCallback(async (forceRefresh = false) => {
    if (!localReportStartDate || !localReportEndDate) {
      console.log('⚠️ [WorkTimeMobile] fetchLocalReport: нет дат', { localReportStartDate, localReportEndDate });
      return;
    }
    const cache = prefetchedLocalReport.current;
    if (!forceRefresh && localReportStartDate === localReportEndDate && cache?.date === localReportStartDate && Array.isArray(cache?.report)) {
      setLocalReportUsers(cache.report);
      setLoadingLocalReport(false);
      return;
    }
    console.log('📥 [WorkTimeMobile] Загрузка локального отчета:', { start: localReportStartDate, end: localReportEndDate, forceRefresh });
    setLoadingLocalReport(true);
    try {
      const url = `/api/local-worktime-report?start=${localReportStartDate}&end=${localReportEndDate}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success && Array.isArray(data.report)) {
        setLocalReportUsers(data.report);
        if (localReportStartDate === localReportEndDate) {
          prefetchedLocalReport.current = { date: localReportStartDate, report: data.report };
        }
      } else {
        setLocalReportUsers([]);
      }
    } catch (error) {
      console.error('❌ [WorkTimeMobile] Ошибка загрузки локального отчета:', error);
      setLocalReportUsers([]);
    }
    setLoadingLocalReport(false);
  }, [localReportStartDate, localReportEndDate]);

  // Автоматически обновляем currentDate при изменении startDate через календарь (если startDate === endDate)
  useEffect(() => {
    if (localReportStartDate === localReportEndDate) {
      setLocalReportCurrentDate(localReportStartDate);
    }
  }, [localReportStartDate, localReportEndDate]);

  // Автозагрузка при открытии модалки и изменении дат
  useEffect(() => {
    if (showLocalReport && localReportStartDate && localReportEndDate) {
      fetchLocalReport();
    }
  }, [showLocalReport, localReportStartDate, localReportEndDate, fetchLocalReport]);

  // Автообновление отчёта раз в минуту (всегда с сервера, чтобы видеть новых сотрудников)
  useEffect(() => {
    if (!showLocalReport || !localReportStartDate || !localReportEndDate) return;
    const interval = setInterval(() => { fetchLocalReport(true); }, 60000);
    return () => clearInterval(interval);
  }, [showLocalReport, localReportStartDate, localReportEndDate, fetchLocalReport]);

  // Заполнить первый/последний вход из приложений из отчёта (чтобы колонка «Отработано» была корректна без открытия модалки «Подробнее»)
  useEffect(() => {
    if (!localReportUsers?.length || !localReportStartDate || !localReportEndDate) return;
    setFirstLoginFromApplications(prev => {
      const next = { ...prev };
      localReportUsers.forEach(row => {
        const key = `${row.username}-${localReportStartDate}-${localReportEndDate}`;
        if (row.firstLoginFromApplications) next[key] = row.firstLoginFromApplications;
      });
      return next;
    });
    setLastLogoutFromApplications(prev => {
      const next = { ...prev };
      localReportUsers.forEach(row => {
        const key = `${row.username}-${localReportStartDate}-${localReportEndDate}`;
        if (row.lastLogoutFromApplications) next[key] = row.lastLogoutFromApplications;
      });
      return next;
    });
  }, [localReportUsers, localReportStartDate, localReportEndDate]);

  // Параметры открытой модалки «Подробнее» (для опроса без пересоздания callback)
  const localDetailsParamsRef = useRef({ open: false, realUsername: '', startDate: '', endDate: '' });
  localDetailsParamsRef.current = {
    open: localReportDetailsModal.open,
    realUsername: localReportDetailsModal.realUsername || '',
    startDate: localReportDetailsModal.startDate || '',
    endDate: localReportDetailsModal.endDate || ''
  };

  // Обновление данных в модалке «Подробнее» (сайты, скрины, приложения, время) каждую секунду
  useEffect(() => {
    if (!localReportDetailsModal.open) return;
    const refresh = async () => {
      const p = localDetailsParamsRef.current;
      if (!p.open || !p.realUsername || !p.startDate || !p.endDate) return;
      try {
        const params = new URLSearchParams({ username: p.realUsername, start: p.startDate, end: p.endDate });
        const res = await fetch(`/api/activity-details?${params.toString()}`);
        const data = await res.json();
        if (res.ok && data.success) {
          const apps = data.applications || [];
          const firstAppTime = apps.length
            ? apps.reduce((minTs, app) => {
                const t = app.timestamp;
                if (!t) return minTs;
                return !minTs || (new Date(t).getTime() < new Date(minTs).getTime()) ? t : minTs;
              }, null)
            : null;
          const lastAppTime = apps.length
            ? apps.reduce((maxTs, app) => {
                const t = app.timestamp;
                if (!t) return maxTs;
                return !maxTs || (new Date(t).getTime() > new Date(maxTs).getTime()) ? t : maxTs;
              }, null)
            : null;
          if (firstAppTime && p.realUsername && p.startDate && p.endDate) {
            const key = `${p.realUsername}-${p.startDate}-${p.endDate}`;
            setFirstLoginFromApplications(prev => ({ ...prev, [key]: firstAppTime }));
          }
          if (lastAppTime && p.realUsername && p.startDate && p.endDate) {
            const key = `${p.realUsername}-${p.startDate}-${p.endDate}`;
            setLastLogoutFromApplications(prev => ({ ...prev, [key]: lastAppTime }));
          }
          if (p.realUsername && p.startDate && p.endDate) {
            const key = `${p.realUsername}-${p.startDate}-${p.endDate}`;
            setDetailsAvailability(prev => ({
              ...prev,
              [key]: {
                hasApplications: (apps || []).length > 0,
                hasScreenshots: (data.screenshots || data.photos || []).length > 0
              }
            }));
          }
          setLocalReportDetailsModal(prev => ({
            ...prev,
            // Не затирать имеющиеся данные пустым ответом (при таймауте/ошибке ответ может прийти без массивов)
            urls: (data.urls && data.urls.length > 0) ? data.urls : (prev.urls?.length ? prev.urls : (data.urls || [])),
            applications: (apps && apps.length > 0) ? apps : (prev.applications?.length ? prev.applications : (apps || [])),
            screenshots: ((data.screenshots || data.photos) && (data.screenshots || data.photos).length > 0) ? (data.screenshots || data.photos) : (prev.screenshots?.length ? prev.screenshots : (data.screenshots || data.photos || [])),
            activityStats: data.activityStats ?? prev.activityStats
          }));
        }
      } catch (e) { /* ignore */ }
    };
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [localReportDetailsModal.open]);

  // Лёгкая мета для таблицы — без полной выгрузки activity-details по каждой строке
  const localReportFetchingKeysRef = React.useRef(new Set());

  // Фильтрация пользователей по поиску (объявление выше useEffect, который её использует)
  const filteredLocalReportUsers = React.useMemo(() => {
    if (!localReportSearchTerm.trim()) return localReportUsers;
    return localReportUsers.filter((user) => matchesLocalReportSearch(user, localReportSearchTerm));
  }, [localReportUsers, localReportSearchTerm]);

  useEffect(() => {
    if (!showLocalReport || !localReportStartDate || !localReportEndDate) return;
    const start = localReportStartDate;
    const end = localReportEndDate;
    const need = filteredLocalReportUsers.filter((user) => {
      const key = `${user.username}-${start}-${end}`;
      if (detailsAvailability[key]) return false;
      if (localReportFetchingKeysRef.current.has(key)) return false;
      return true;
    });
    if (!need.length) return;
    need.forEach((user) => localReportFetchingKeysRef.current.add(`${user.username}-${start}-${end}`));
    const params = new URLSearchParams({
      start,
      end,
      usernames: need.map((u) => u.username).filter(Boolean).join(',')
    });
    fetch(`/api/activity-details-meta?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        const byUser = data?.byUser || {};
        setDetailsAvailability((prev) => {
          const next = { ...prev };
          need.forEach((user) => {
            const key = `${user.username}-${start}-${end}`;
            const meta = byUser[user.username] || {};
            next[key] = {
              hasApplications: !!meta.hasApplications,
              hasScreenshots: !!meta.hasScreenshots
            };
          });
          return next;
        });
        setLastLogoutFromApplications((prev) => {
          const next = { ...prev };
          need.forEach((user) => {
            const meta = byUser[user.username];
            if (meta?.lastAppTime) next[`${user.username}-${start}-${end}`] = meta.lastAppTime;
          });
          return next;
        });
        setFirstLoginFromApplications((prev) => {
          const next = { ...prev };
          need.forEach((user) => {
            const meta = byUser[user.username];
            if (meta?.firstAppTime) next[`${user.username}-${start}-${end}`] = meta.firstAppTime;
          });
          return next;
        });
      })
      .catch(() => {})
      .finally(() => {
        need.forEach((user) => localReportFetchingKeysRef.current.delete(`${user.username}-${start}-${end}`));
      });
  }, [showLocalReport, filteredLocalReportUsers, localReportStartDate, localReportEndDate, detailsAvailability]);

  // Обработка поиска в локальном отчете
  const handleLocalReportSearchChange = (e) => {
    const value = e.target.value;
    setLocalReportSearchTerm(value);
    const labels = localReportSearchLabels(
      localReportUsers.length ? localReportUsers : localReportUsersList.map((u) => ({ fio: u, username: u }))
    );
    if (value.length > 0) {
      setLocalReportShowAutocomplete(true);
      const q = value.toLowerCase();
      setLocalReportUserOptions(labels.filter((label) => label.toLowerCase().includes(q)));
    } else {
      setLocalReportShowAutocomplete(false);
      setLocalReportUserOptions(labels);
    }
  };

  // Удаление данных за период дат
  const handleDeleteByDateRange = async (start, end) => {
    if (!start || !end) {
      alert('Выберите период дат для удаления');
      return;
    }
    
    if (!window.confirm(`Вы уверены, что хотите удалить все данные активности за период с ${start} по ${end}? Это действие нельзя отменить.`)) {
      return;
    }
    
    setLoadingLocalReport(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/activity-logs/clear', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ start, end })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        alert(`✅ ${data.message || `Удалено ${data.deletedCount || 0} записей`}`);
        // Обновляем отчет после удаления (принудительно с сервера)
        if (fetchLocalReport) {
          fetchLocalReport(true);
        }
      } else {
        alert(`❌ Ошибка: ${data.error || 'Не удалось удалить данные'}`);
      }
    } catch (error) {
      console.error('Ошибка удаления данных:', error);
      alert('❌ Ошибка при удалении данных');
    }
    setLoadingLocalReport(false);
  };

  // Удаление данных за период (день/неделя/месяц)
  const handleDeleteByPeriod = async (period) => {
    const periodNames = {
      'day': 'сегодня',
      'week': 'последнюю неделю',
      'month': 'последний месяц'
    };
    if (!window.confirm(`Вы уверены, что хотите удалить все данные активности за ${periodNames[period] || period}? Это действие нельзя отменить.`)) {
      return;
    }
    
    setLoadingLocalReport(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/activity-logs/clear', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ period })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        alert(`✅ ${data.message || `Удалено ${data.deletedCount || 0} записей`}`);
        // Обновляем отчет после удаления (принудительно с сервера)
        if (fetchLocalReport) {
          fetchLocalReport(true);
        }
      } else {
        alert(`❌ Ошибка: ${data.error || 'Не удалось удалить данные'}`);
      }
    } catch (error) {
      console.error('Ошибка удаления данных:', error);
      alert('❌ Ошибка при удалении данных');
    }
    setLoadingLocalReport(false);
  };

  // Открытие деталей пользователя: модалка открывается сразу, детали подгружаются в фоне
  const handleOpenLocalUserDetails = (user) => {
    const expandedStart = localReportStartDate || '';
    const expandedEnd = localReportEndDate || '';
    // Сразу открываем модалку с базовыми данными
    setLocalReportDetailsModal({
      open: true,
      logs: user.sessions || [],
      username: user.fio || user.username,
      realUsername: user.username,
      activityStats: null,
      urls: [],
      applications: [],
      screenshots: [],
      startDate: expandedStart,
      endDate: expandedEnd
    });
    // Детали (сайты, приложения, скрины) подгружаем в фоне
    const detailsParams = new URLSearchParams({
      username: user.username || '',
      start: expandedStart,
      end: expandedEnd
    });
    fetch(`/api/activity-details?${detailsParams.toString()}`)
      .then(res => res.json())
      .then(detailsData => {
        if (detailsData.success) {
          const apps = detailsData.applications || [];
          const firstAppTime = apps.length
            ? apps.reduce((minTs, app) => {
                const t = app.timestamp;
                if (!t) return minTs;
                return !minTs || (new Date(t).getTime() < new Date(minTs).getTime()) ? t : minTs;
              }, null)
            : null;
          const lastAppTime = apps.length
            ? apps.reduce((maxTs, app) => {
                const t = app.timestamp;
                if (!t) return maxTs;
                return !maxTs || (new Date(t).getTime() > new Date(maxTs).getTime()) ? t : maxTs;
              }, null)
            : null;
          const key = `${user.username}-${expandedStart}-${expandedEnd}`;
          if (firstAppTime) {
            setFirstLoginFromApplications(prev => ({ ...prev, [key]: firstAppTime }));
          }
          if (lastAppTime) {
            setLastLogoutFromApplications(prev => ({ ...prev, [key]: lastAppTime }));
          }
          setDetailsAvailability(prev => ({
            ...prev,
            [key]: {
              hasApplications: (apps || []).length > 0,
              hasScreenshots: (detailsData.screenshots || detailsData.photos || []).length > 0
            }
          }));
          setLocalReportDetailsModal(prev => prev.open && prev.realUsername === user.username ? {
            ...prev,
            activityStats: detailsData.activityStats ?? prev.activityStats,
            urls: detailsData.urls || [],
            applications: apps,
            screenshots: detailsData.screenshots || detailsData.photos || []
          } : prev);
        }
      })
      .catch(() => {});
  };

  // Автоматически загружаем данные при открытии модалки, если дата сегодняшняя
  useEffect(() => {
    if (open) {
      const today = getTodayLocalDate();
      if (startDate === today && endDate === today) {
        // Загружаем данные автоматически
        (async () => {
          setLoading(true);
          try {
            // Сервер сам расширяет диапазон для учёта часового пояса
            // Используем endpoint для ЛОКАЛЬНЫХ данных
            let url = `/api/local-worktime-report?start=${startDate}&end=${endDate}`;
            if (selectedUser) url += `&username=${encodeURIComponent(selectedUser)}`;
            const res = await fetch(url);
            const data = await res.json();
            console.log('📊 [WorkTimeMobile] Данные из local-worktime-report (локальные, автозагрузка):', data);
            if (data.report && data.report.length > 0) {
              console.log('📊 [WorkTimeMobile] Первый элемент отчёта:', JSON.stringify(data.report[0], null, 2));
              console.log('📊 [WorkTimeMobile] FIO:', data.report[0].fio, 'Username:', data.report[0].username);
              console.log('📊 [WorkTimeMobile] Sessions:', data.report[0].sessions?.length || 0, 'sessions');
            } else {
              console.warn('⚠️ [WorkTimeMobile] Нет данных в отчёте!', data);
            }
            setLogs(Array.isArray(data.report) ? data.report : []);
          } catch {
            setLogs([]);
          }
          setLoading(false);
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startDate, endDate]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length > 0) {
      setShowAutocomplete(true);
      setUserOptions(usersList.filter(u => u.toLowerCase().includes(value.toLowerCase())));
    } else {
      setShowAutocomplete(false);
      setUserOptions(usersList);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user);
    setShowAutocomplete(false);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedUser('');
    setShowAutocomplete(false);
    setUserOptions(usersList);
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      // Сервер сам расширяет диапазон для учёта часового пояса
      // Используем endpoint для ЛОКАЛЬНЫХ данных
      let url = `/api/local-worktime-report?start=${startDate}&end=${endDate}`;
      if (selectedUser) url += `&username=${encodeURIComponent(selectedUser)}`;
      const res = await fetch(url);
      const data = await res.json();
      console.log('📊 [WorkTimeMobile] Данные из local-worktime-report (локальные):', data);
      if (data.report && data.report.length > 0) {
        console.log('📊 [WorkTimeMobile] Первый элемент отчёта:', JSON.stringify(data.report[0], null, 2));
        console.log('📊 [WorkTimeMobile] FIO:', data.report[0].fio, 'Username:', data.report[0].username, 'Sessions:', data.report[0].sessions?.length || 0);
      } else {
        console.warn('⚠️ [WorkTimeMobile] Нет данных в отчёте!', data);
      }
      setLogs(Array.isArray(data.report) ? data.report : []);
    } catch {
      setLogs([]);
    }
    setLoading(false);
  };

  const triggerYesterdayImport = async () => {
    setImporting(true);
    setImportOk(null);
    try {
      const res = await fetch('/api/auto-import-csv-yesterday', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      const ok = res.ok && data && data.success && typeof data.imported === 'number' && data.imported > 0;
      setImportOk(ok);
    } catch {
      setImportOk(false);
    }
    setImporting(false);
  };


  // Фильтрация строк таблицы
  const displayedRows = React.useMemo(() => {
    if (!Array.isArray(logs) || logs.length === 0) return [];
    const q = (searchTerm || '').trim().toLowerCase();
    return logs.filter(row => {
      const fio = (row.fio || row.username || '').trim();
      if (selectedUser) return fio === selectedUser;
      if (q) return fio.toLowerCase().includes(q);
      return true;
    });
  }, [logs, selectedUser, searchTerm]);

  // Обработчики свайпа
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!ENABLE_MODAL_SWIPE_BACK) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      onClose();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleClose = () => {
    // Просто закрываем модалку, возвращаемся к SidebarNav (который остается открытым)
    onClose();
  };

  // Рендерим модалки RemoteWorktime ВНЕ портала WorkTimeMobile, чтобы они всегда были доступны
  const remoteWorktimeModals = (
    <>
      <RemoteWorktimeReportMobile
        open={showRemoteWorktime}
        onClose={() => {
          console.log('🔴 [WorkTimeMobile] Закрываем RemoteWorktimeReportMobile');
          setShowRemoteWorktime(false);
        }}
        onOpenMobileSidebar={() => {
          // Не открываем сайдбар, так как мы уже внутри модалки
        }}
      />
      <RemoteWorktimeReportModal 
        isOpen={showRemoteWorktime} 
        onRequestClose={() => {
          console.log('🔴 [WorkTimeMobile] Закрываем RemoteWorktimeReportModal (desktop)');
          setShowRemoteWorktime(false);
        }} 
      />
    </>
  );

  return (
    <>
      {ReactDOM.createPortal(remoteWorktimeModals, document.body)}
      {/* Основная модалка "Мониторинг времени" - скрываем, когда открыта модалка локального отчета или деталей */}
      {open && !showLocalReport && !localReportDetailsModal.open && !showAppUsage && !showRemoteWorktime && !showOneCHistoryModal && ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'transparent',
        zIndex: 100002, // Выше SidebarNav (100001)
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          paddingTop: 0,
          paddingBottom: 0,
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
        {...(ENABLE_MODAL_SWIPE_BACK
          ? { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd }
          : {})}
      >
        <WorkTimeHub
          isMobile
          onClose={handleClose}
          onOpenLaunch={() => setShowAppUsage(true)}
          onOpenRemote={() => setShowRemoteWorktime(true)}
          onOpenLocal={() => setShowLocalReport(true)}
          onOpenOneC={() => setShowOneCHistoryModal(true)}
        />

        {/* Модалки */}
        {detailsModal.open && console.log('🔍 [WorkTimeMobile] Рендер модалки деталей:', { isMobile, open: detailsModal.open, logsCount: detailsModal.logs?.length, username: detailsModal.username })}
        {isMobile ? (
          <UserWorkTimeDetailsMobile
            open={detailsModal.open}
            onClose={() => {
              console.log('🔴 [WorkTimeMobile] Закрываем модалку деталей');
              setDetailsModal({ 
                open: false, 
                logs: [], 
                username: '',
                realUsername: '',
                activityStats: null,
                urls: [],
                applications: [],
                screenshots: [],
                startDate: '',
                endDate: '',
              });
            }}
            logs={detailsModal.logs}
            username={detailsModal.username}
            realUsername={detailsModal.realUsername}
            activityStats={detailsModal.activityStats}
            urls={detailsModal.urls || []}
            applications={detailsModal.applications || []}
            screenshots={detailsModal.screenshots || []}
            startDate={detailsModal.startDate}
            endDate={detailsModal.endDate}
            onOpenMobileSidebar={() => {
              // Не открываем сайдбар, так как мы уже внутри модалки
            }}
          />
        ) : (
          <UserWorkTimeDetailsModal
            isOpen={detailsModal.open}
            onRequestClose={() => {
              console.log('🔴 [WorkTimeMobile] Закрываем модалку деталей (desktop)');
              setDetailsModal({ 
                open: false, 
                logs: [], 
                username: '',
                realUsername: '',
                activityStats: null,
                urls: [],
                applications: [],
                screenshots: [],
                startDate: '',
                endDate: '',
              });
            }}
            logs={detailsModal.logs}
            username={detailsModal.username}
            activityStats={detailsModal.activityStats}
            urls={detailsModal.urls || []}
            applications={detailsModal.applications || []}
            screenshots={detailsModal.screenshots || []}
            startDate={detailsModal.startDate}
            endDate={detailsModal.endDate}
          />
        )}
      </div>
    </div>,
    document.body
      )}
      {/* Модалки рендерятся вне портала WorkTimeMobile, чтобы они были поверх */}
      {isMobile ? (
        <AppUsageMobile
          open={showAppUsage}
          onClose={() => setShowAppUsage(false)}
          onOpenMobileSidebar={() => {
            // Не открываем сайдбар, так как мы уже внутри модалки
          }}
        />
      ) : (
        <AppUsageModal isOpen={showAppUsage} onRequestClose={() => setShowAppUsage(false)} />
      )}
      {console.log('🔍 [WorkTimeMobile] Рендер модалок RemoteWorktime, isMobile =', isMobile, 'showRemoteWorktime =', showRemoteWorktime)}
      {/* Рендерим обе версии модалок одновременно, чтобы гарантировать отображение */}
      <RemoteWorktimeReportMobile
        open={showRemoteWorktime}
        onClose={() => {
          console.log('🔴 [WorkTimeMobile] Закрываем RemoteWorktimeReportMobile');
          setShowRemoteWorktime(false);
        }}
        onOpenMobileSidebar={() => {
          // Не открываем сайдбар, так как мы уже внутри модалки
        }}
      />
      <RemoteWorktimeReportModal 
        isOpen={showRemoteWorktime} 
        onRequestClose={() => {
          console.log('🔴 [WorkTimeMobile] Закрываем RemoteWorktimeReportModal (desktop)');
          setShowRemoteWorktime(false);
        }} 
      />
      
      {/* Модалка История 1С */}
      <OneCDocumentHistoryMobile
        open={showOneCHistoryModal}
        onClose={() => setShowOneCHistoryModal(false)}
        onOpenMobileSidebar={() => {}}
      />
      
      {/* Модалка локального отчета - рендерится вне основного портала */}
      {/* Скрываем модалку локального отчета, когда открыта модалка деталей */}
      {showLocalReport && (
        <LocalWorktimeReportModalMobile
          open={showLocalReport && !localReportDetailsModal.open}
          onClose={() => {
            console.log('🔴 [WorkTimeMobile] Закрываем модалку локального отчета');
            setShowLocalReport(false);
            setLocalReportUsers([]);
            setLocalReportSearchTerm('');
          }}
          currentDate={localReportCurrentDate}
          onCurrentDateChange={(newDate) => {
            setLocalReportCurrentDate(newDate);
            setLocalReportStartDate(newDate);
            setLocalReportEndDate(newDate);
            setLoadingLocalReport(true);
            fetch(`/api/local-worktime-report?start=${newDate}&end=${newDate}`)
              .then(res => res.json())
              .then(data => {
                if (data.success && Array.isArray(data.report)) {
                  setLocalReportUsers(data.report);
                } else {
                  setLocalReportUsers([]);
                }
              })
              .catch(error => {
                console.error('Ошибка загрузки локального отчета:', error);
                setLocalReportUsers([]);
              })
              .finally(() => {
                setLoadingLocalReport(false);
              });
          }}
          onPreviousDay={handleLocalReportPreviousDay}
          onNextDay={handleLocalReportNextDay}
          startDate={localReportStartDate}
          endDate={localReportEndDate}
          onStartDateChange={setLocalReportStartDate}
          onEndDateChange={setLocalReportEndDate}
          searchTerm={localReportSearchTerm}
          onSearchChange={handleLocalReportSearchChange}
          users={filteredLocalReportUsers}
          loading={loadingLocalReport}
          onUserClick={handleOpenLocalUserDetails}
          userOptions={localReportUserOptions}
          showAutocomplete={localReportShowAutocomplete}
          onSelectUser={(user) => {
            setLocalReportSearchTerm(user);
            setLocalReportShowAutocomplete(false);
          }}
          formatTime={formatTime}
          onFetchReport={() => fetchLocalReport(true)}
          onDeleteByDateRange={handleDeleteByDateRange}
          onDeleteByPeriod={handleDeleteByPeriod}
          firstLoginFromApplications={firstLoginFromApplications}
          lastLogoutFromApplications={lastLogoutFromApplications}
          detailsAvailability={detailsAvailability}
        />
      )}

      {/* Модалка деталей пользователя для локального отчета */}
      {localReportDetailsModal.open && (
        isMobile ? (
          <UserWorkTimeDetailsMobile
            open={localReportDetailsModal.open}
            onClose={() => {
              console.log('🔴 [WorkTimeMobile] Закрываем модалку деталей локального отчета');
              setLocalReportDetailsModal({
                open: false,
                logs: [],
                username: '',
                realUsername: '',
                activityStats: null,
                urls: [],
                applications: [],
                screenshots: [],
                startDate: '',
                endDate: '',
              });
            }}
            logs={localReportDetailsModal.logs}
            username={localReportDetailsModal.username}
            realUsername={localReportDetailsModal.realUsername}
            activityStats={localReportDetailsModal.activityStats}
            urls={localReportDetailsModal.urls || []}
            applications={localReportDetailsModal.applications || []}
            screenshots={localReportDetailsModal.screenshots || []}
            startDate={localReportDetailsModal.startDate}
            endDate={localReportDetailsModal.endDate}
            onOpenMobileSidebar={() => {
              // Не открываем сайдбар, так как мы уже внутри модалки
            }}
          />
        ) : (
          <UserWorkTimeDetailsMobile
            open={localReportDetailsModal.open}
            onClose={() => {
              console.log('🔴 [WorkTimeMobile] Закрываем модалку деталей локального отчета (desktop)');
              setLocalReportDetailsModal({
                open: false,
                logs: [],
                username: '',
                realUsername: '',
                activityStats: null,
                urls: [],
                applications: [],
                screenshots: [],
                startDate: '',
                endDate: '',
              });
            }}
            logs={localReportDetailsModal.logs}
            username={localReportDetailsModal.username}
            realUsername={localReportDetailsModal.realUsername}
            activityStats={localReportDetailsModal.activityStats}
            urls={localReportDetailsModal.urls || []}
            applications={localReportDetailsModal.applications || []}
            screenshots={localReportDetailsModal.screenshots || []}
            startDate={localReportDetailsModal.startDate}
            endDate={localReportDetailsModal.endDate}
            onOpenMobileSidebar={() => {
              // Не открываем сайдбар на десктопе
            }}
          />
        )
      )}
    </>
  );
}

// Модалка локального отчета - отдельный компонент
function LocalWorktimeReportModalMobile({ 
  open, 
  onClose, 
  currentDate,
  onCurrentDateChange,
  onPreviousDay,
  onNextDay,
  startDate, 
  endDate, 
  onStartDateChange, 
  onEndDateChange,
  searchTerm,
  onSearchChange,
  users,
  loading,
  onUserClick,
  userOptions,
  showAutocomplete,
  onSelectUser,
  formatTime,
  onFetchReport,
  onDeleteByDateRange,
  onDeleteByPeriod,
  firstLoginFromApplications = {},
  lastLogoutFromApplications = {},
  detailsAvailability = {},
}) {
  const modalRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const [ndClock, setNdClock] = useState(() => new Date());
  const [ndRange, setNdRange] = useState('day');
  const [hideFioDuplicates, setHideFioDuplicates] = useState(() => {
    try {
      return localStorage.getItem('local-report-hide-fio-dups') !== '0';
    } catch (_) {
      return true;
    }
  });

  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setNdClock(new Date()), 1000);
    return () => clearInterval(id);
  }, [open]);

  // Диапазон считаем локально и отдаём наружу уже готовые границы периода
  const applyNdRange = (baseDate, range) => {
    if (!baseDate) return;
    const base = new Date(`${baseDate}T00:00:00`);
    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let from = base;
    let to = base;
    if (range === 'week') {
      const shift = (base.getDay() + 6) % 7;
      from = new Date(base.getFullYear(), base.getMonth(), base.getDate() - shift);
      to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
    } else if (range === 'month') {
      from = new Date(base.getFullYear(), base.getMonth(), 1);
      to = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    }
    onCurrentDateChange && onCurrentDateChange(baseDate);
    onStartDateChange && onStartDateChange(ymd(from));
    onEndDateChange && onEndDateChange(ymd(to));
  };

  const formatWorkTimeMins = (mins) => {
    if (mins === 0) return '0м';
    if (!mins || mins < 0) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h > 0 ? h + 'ч ' : ''}${m}м`;
  };

  const getOvertimeMinutes = (firstLogin, lastLogout, workStartHour = 9, workEndHour = 18) => {
    const start = new Date(firstLogin);
    const end = new Date(lastLogout);
    if (end <= start) return 0;
    let overtime = 0;
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= endDay) {
      const workStart = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), workStartHour, 0, 0, 0);
      const workEnd = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), workEndHour, 0, 0, 0);
      const dayEnd = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), 23, 59, 59, 999);
      const sessionOnDayStart = start > cur ? start : cur;
      const sessionOnDayEnd = end < dayEnd ? end : dayEnd;
      if (sessionOnDayStart < sessionOnDayEnd) {
        if (sessionOnDayStart < workStart) {
          const earlyEnd = workStart < sessionOnDayEnd ? workStart : sessionOnDayEnd;
          overtime += (earlyEnd.getTime() - sessionOnDayStart.getTime()) / 60000;
        }
        if (sessionOnDayEnd > workEnd) {
          const lateStart = workEnd > sessionOnDayStart ? workEnd : sessionOnDayStart;
          overtime += (sessionOnDayEnd.getTime() - lateStart.getTime()) / 60000;
        }
      }
      cur.setDate(cur.getDate() + 1);
    }
    return Math.round(overtime);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!ENABLE_MODAL_SWIPE_BACK) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 50) {
      onClose();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (!open) {
    console.log('🚫 [LocalWorktimeReportModalMobile] Модалка закрыта, не рендерим');
    return null;
  }

  console.log('✅ [LocalWorktimeReportModalMobile] Рендерим модалку локального отчета');

  const ND_WORK_DAY_MINUTES = 9 * 60;
  const ND_AVATAR_COLORS = ['#22d3ee', '#2dd4bf', '#38bdf8', '#a78bfa', '#fbbf24', '#f0566a', '#34d399'];

  const ndRowsRaw = (Array.isArray(users) ? users : []).map((row, idx) => {
    const key = `${row.username}-${startDate}-${endDate}`;
    const firstLogin = firstLoginFromApplications[key] || null;
    const lastLogout = lastLogoutFromApplications[key] || null;
    let minutes = 0;
    let overtime = 0;
    if (firstLogin && lastLogout) {
      minutes = Math.max(0, Math.round((new Date(lastLogout).getTime() - new Date(firstLogin).getTime()) / 60000));
      overtime = getOvertimeMinutes(firstLogin, lastLogout);
    }
    const rawName = row.fio && !row.fio.includes('?') && row.fio.trim() ? row.fio : (row.username || 'Неизвестный');
    const name = toCyrillicFio(rawName);
    const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?';
    return {
      row,
      idx,
      key,
      firstLogin,
      lastLogout,
      minutes,
      overtime,
      worked: minutes,
      name,
      initials,
      avail: detailsAvailability[key],
      first: firstLogin,
      last: lastLogout,
    };
  });

  const fioDuplicateCount = countHiddenFioDuplicates(ndRowsRaw);
  const ndRows = hideFioDuplicates
    ? hideDuplicateFioRows(ndRowsRaw, detailsAvailability).map((item) => ({
        ...item,
        minutes: item.worked,
        initials: (item.name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?',
        avail: detailsAvailability[item.key],
      }))
    : ndRowsRaw;

  const ndWithData = ndRows.filter(r => r.minutes > 0);
  const ndStats = {
    total: ndRows.length,
    online: ndWithData.length,
    avg: ndWithData.length ? Math.round(ndWithData.reduce((s, r) => s + r.minutes, 0) / ndWithData.length) : 0,
    overtime: ndRows.reduce((s, r) => s + r.overtime, 0),
    absent: ndRows.length - ndWithData.length,
  };

  const ndWeekday = ndClock.toLocaleDateString('ru-RU', { weekday: 'long' });

  return ReactDOM.createPortal(
    <div
      ref={modalRef}
      className="nd-report nd-mobile"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100003 }}
      onClick={(e) => e.stopPropagation()}
      {...(ENABLE_MODAL_SWIPE_BACK
        ? { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd }
        : {})}
    >
      <div className="nd-topbar">
        <h1>
          <FiMonitor size={15} style={{ verticalAlign: '-2px', marginRight: 7 }} />
          Активность локальных ПК
          <span>мониторинг рабочего времени · реальное время</span>
        </h1>

        <button
          type="button"
          className="nd-themeBtn"
          onClick={toggleNdTheme}
          title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
        >
          {ndIsLight ? '🌙' : '☀️'}
        </button>

        <button type="button" className="nd-closeBtn" onClick={onClose} title="Закрыть" aria-label="Закрыть">
          ✕
        </button>

        <div className="nd-clockPill">
          <span className="nd-t">{ndClock.toLocaleTimeString('ru-RU')}</span>
          <i />
          <span className="nd-d">{ndWeekday.charAt(0).toUpperCase() + ndWeekday.slice(1)}</span>
          <i />
          <span className="nd-d">{ndClock.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}</span>
        </div>
      </div>

      <div className="nd-content">
        <div className="nd-ctrlRow">
          <div className="nd-dateNav">
            <button type="button" className="nd-dBtn" onClick={onPreviousDay} title="Предыдущий день">
              <FiChevronLeft size={18} />
            </button>
            <input
              type="date"
              className="nd-dateLbl"
              value={currentDate}
              onChange={e => onCurrentDateChange(e.target.value)}
            />
            <button type="button" className="nd-dBtn" onClick={onNextDay} title="Следующий день">
              <FiChevronRight size={18} />
            </button>
          </div>

          <div className="nd-rangeChips">
            {[{ k: 'day', l: 'День' }, { k: 'week', l: 'Неделя' }, { k: 'month', l: 'Месяц' }].map(chip => (
              <button
                key={chip.k}
                type="button"
                className={ndRange === chip.k ? 'nd-rChip nd-on' : 'nd-rChip'}
                onClick={() => {
                  setNdRange(chip.k);
                  applyNdRange(currentDate, chip.k);
                }}
              >
                {chip.l}
              </button>
            ))}
          </div>

          {startDate !== endDate && (
            <span className="nd-mono" style={{ fontSize: 11, fontWeight: 700, color: 'var(--nd-mut)' }}>
              {startDate} — {endDate}
            </span>
          )}

          <div className="nd-searchW">
            <input
              type="text"
              placeholder="Поиск по имени…"
              value={searchTerm}
              onChange={onSearchChange}
            />
            {showAutocomplete && userOptions && userOptions.length > 0 && (
              <div className="nd-autocomplete">
                {userOptions.map((u, i) => (
                  <div key={u + i} onMouseDown={() => onSelectUser(u)}>{u}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="nd-statsRow">
          <div className="nd-statCard nd-glass" style={{ '--nd-sc': 'var(--nd-green)', animationDelay: '0ms' }}>
            <div className="nd-stIco">🟢</div>
            <div>
              <div className="nd-stV">{ndStats.online}</div>
              <div className="nd-stL">Из {ndStats.total}</div>
            </div>
          </div>
          <div className="nd-statCard nd-glass" style={{ '--nd-sc': 'var(--nd-acc2)', animationDelay: '60ms' }}>
            <div className="nd-stIco">⏱</div>
            <div>
              <div className="nd-stV">{ndStats.avg > 0 ? formatWorkTimeMins(ndStats.avg) : '—'}</div>
              <div className="nd-stL">Среднее</div>
            </div>
          </div>
          <div className="nd-statCard nd-glass" style={{ '--nd-sc': 'var(--nd-amber)', animationDelay: '120ms' }}>
            <div className="nd-stIco">🔥</div>
            <div>
              <div className="nd-stV">{ndStats.overtime > 0 ? formatWorkTimeMins(ndStats.overtime) : '—'}</div>
              <div className="nd-stL">Переработка</div>
            </div>
          </div>
          <div className="nd-statCard nd-glass" style={{ '--nd-sc': 'var(--nd-red)', animationDelay: '180ms' }}>
            <div className="nd-stIco">🚫</div>
            <div>
              <div className="nd-stV">{ndStats.absent}</div>
              <div className="nd-stL">Без данных</div>
            </div>
          </div>
        </div>

        <div className="nd-actRow">
          <button
            type="button"
            className="nd-btn nd-go"
            disabled={loading}
            onClick={() => { if (onFetchReport) onFetchReport(); }}
          >
            <FiRefreshCw size={14} /> {loading ? 'Загрузка…' : 'Обновить отчёт'}
          </button>

          <button
            type="button"
            className={hideFioDuplicates ? 'nd-btn nd-go' : 'nd-btn nd-ghost'}
            onClick={() => {
              setHideFioDuplicates((v) => {
                const next = !v;
                try {
                  localStorage.setItem('local-report-hide-fio-dups', next ? '1' : '0');
                } catch (_) {}
                return next;
              });
            }}
            title="Скрыть строки с одинаковым ФИО. Данные не удаляются."
          >
            {hideFioDuplicates
              ? `👤 Дубли скрыты${fioDuplicateCount ? ` (−${fioDuplicateCount})` : ''}`
              : `👤 Скрыть дубли${fioDuplicateCount ? ` (${fioDuplicateCount})` : ''}`}
          </button>

          <button
            type="button"
            className="nd-btn nd-dangerBtn"
            disabled={loading || !startDate || !endDate}
            onClick={() => {
              if (!startDate || !endDate) {
                alert('Выберите период дат для удаления');
                return;
              }
              if (window.confirm(`Вы уверены, что хотите удалить все данные активности за период с ${startDate} по ${endDate}? Это действие нельзя отменить.`)) {
                onDeleteByDateRange && onDeleteByDateRange(startDate, endDate);
              }
            }}
          >
            🗑 За период
          </button>

          <button
            type="button"
            className="nd-btn nd-ghost"
            disabled={loading}
            onClick={() => {
              if (window.confirm('Вы уверены, что хотите удалить все данные активности за последнюю неделю? Это действие нельзя отменить.')) {
                onDeleteByPeriod && onDeleteByPeriod('week');
              }
            }}
          >
            🗑 Неделя
          </button>

          <button
            type="button"
            className="nd-btn nd-ghost"
            disabled={loading}
            onClick={() => {
              if (window.confirm('Вы уверены, что хотите удалить все данные активности за последний месяц? Это действие нельзя отменить.')) {
                onDeleteByPeriod && onDeleteByPeriod('month');
              }
            }}
          >
            🗑 Месяц
          </button>
        </div>

        {loading ? (
          <div className="nd-empty">Загрузка…</div>
        ) : ndRows.length === 0 ? (
          <div className="nd-empty">Нет данных за выбранный период</div>
        ) : (
          <div className="nd-mList">
            {ndRows.map(item => {
              const pct = Math.min(100, Math.round((item.minutes / ND_WORK_DAY_MINUTES) * 100));
              const noScreenshots = item.avail && !item.avail.hasScreenshots;
              const noApplications = item.avail && !item.avail.hasApplications;
              const noTime = !item.firstLogin;
              const crossed = { position: 'relative', display: 'inline-flex', color: 'var(--nd-mut)', opacity: 0.85 };
              const line = { position: 'absolute', left: 0, top: '50%', width: '100%', height: 1, background: 'var(--nd-red)', transform: 'rotate(-35deg)', pointerEvents: 'none' };
              return (
                <div className="nd-mCard nd-glass" key={item.idx} style={{ animationDelay: `${Math.min(item.idx, 12) * 30}ms` }}>
                  <div className="nd-mCardHead">
                    <div
                      className="nd-wAv"
                      style={{ background: ND_AVATAR_COLORS[item.idx % ND_AVATAR_COLORS.length] }}
                    >
                      {item.initials}
                    </div>
                    <span className={item.minutes > 0 ? 'nd-stDot nd-on' : 'nd-stDot nd-off'} />
                    <div className="nd-mName" title={item.name}>{item.name}</div>
                    {(noScreenshots || noApplications || noTime) && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Нет данных: скриншоты, приложения, время входа">
                        {noScreenshots && <span style={crossed} title="Нет скриншотов"><FiCamera size={15} /><span style={line} /></span>}
                        {noApplications && <span style={crossed} title="Нет приложений"><FiGrid size={15} /><span style={line} /></span>}
                        {noTime && <span style={crossed} title="Нет времени входа"><FiClock size={15} /><span style={line} /></span>}
                      </span>
                    )}
                  </div>

                  <div className="nd-mRow">
                    <span>Первый вход</span>
                    <span>{item.firstLogin ? formatTime(item.firstLogin) : '—'}</span>
                  </div>
                  <div className="nd-mRow">
                    <span>Последний выход</span>
                    <span>{item.lastLogout ? formatTime(item.lastLogout) : '—'}</span>
                  </div>
                  <div className="nd-mRow">
                    <span>Отработано</span>
                    <span style={{ color: item.overtime > 0 ? 'var(--nd-amber)' : 'var(--nd-green)', fontWeight: 800 }}>
                      {item.minutes > 0 ? formatWorkTimeMins(item.minutes) : (item.row.totalTimeStr || '—')}
                      {item.overtime > 0 && (
                        <span style={{ color: 'var(--nd-mut)', fontWeight: 600 }}> +{formatWorkTimeMins(item.overtime)}</span>
                      )}
                    </span>
                  </div>

                  <div className="nd-pBar">
                    <i className={item.overtime > 0 ? 'nd-over' : ''} style={{ width: `${pct}%` }} />
                  </div>

                  <button
                    type="button"
                    className="nd-btn nd-info"
                    style={{ justifyContent: 'center' }}
                    onClick={() => onUserClick(item.row)}
                  >
                    <FiFileText size={14} /> Подробнее
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

